import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { Select as RSelect } from 'chakra-react-select';

import { GlassCard } from '../../../../../components/glass';
import { IPocOption, useContaCilPocs } from '../../../../../hooks/useContaCilLancamentos';
import {
  IContaBancaria,
  useContaCilContas,
  useCriarConta,
  useEditarConta,
  useReidentificarConta,
} from '../../../../../hooks/useContaCilContasBancarias';
import { IBancoOption, useContaCilRegrasBancos } from '../../../../../hooks/useContaCilRegras';
import { extractApiErrorMessage } from '../../../../../utils/ErrorUtils';
import { propsMenuSelect } from '../../../../../utils/selectMenu';

const NAO_IDENTIFICADO = 'Não identificado';
type BancoSelectOption = { value: string; label: string };

// Natureza da conta (tb_contacil_banco_conta.tipo) -> rotulo amigavel.
// Detectada pelo ETL (corrente/poupanca/investimento/salario) ou cadastrada
// manualmente (aplicacao). Valor desconhecido cai no proprio valor cru.
const TIPO_CONTA_LABEL: Record<string, string> = {
  corrente: 'corrente',
  aplicacao: 'aplicação',
  poupanca: 'poupança',
  investimento: 'investimento',
  salario: 'conta-salário',
};

interface PocSelectOption {
  value: string;
  label: string;
}

function ContaRow({
  conta,
  empresaId,
  pocOptions,
  ehDuplicata,
}: {
  conta: IContaBancaria;
  empresaId: number;
  pocOptions: PocSelectOption[];
  ehDuplicata: boolean;
}) {
  const toast = useToast();
  const criar = useCriarConta();
  const editar = useEditarConta();
  const reidentificar = useReidentificarConta();
  const { data: bancosCatalogo } = useContaCilRegrasBancos();
  const [poc, setPoc] = useState<string | null>(conta.poc_codigo ?? null);
  const [novoBanco, setNovoBanco] = useState<string | null>(null);

  // Conta cujo banco o ETL não identificou — deixa o usuário corrigir na mão
  // (o backend migra os lançamentos e mescla com a conta do banco certo, se
  // já existir). Resolve o split "bradesco" + "Não identificado".
  // Deixa corrigir o banco quando a conta é "Não identificado" OU é uma
  // DUPLICATA da mesma ag+conta com rótulo de banco diferente ("itau" vs
  // "BANCO ITAÚ", "bb" vs "banco_do_brasil") — o backend mescla na conta certa.
  const naoIdentificado = conta.banco_nome === NAO_IDENTIFICADO;
  const podeCorrigirBanco = conta.id != null && (naoIdentificado || ehDuplicata);
  // Conta cadastrada com o banco ERRADO (ETL leu o nome de uma contraparte,
  // ex.: "cora" num extrato Domus): a troca fica atras de um link, pra nao
  // poluir toda linha com um seletor de banco.
  const [trocandoBanco, setTrocandoBanco] = useState(false);
  const mostrarCorrecaoBanco = podeCorrigirBanco || (conta.id != null && trocandoBanco);
  const bancoOptions = useMemo<BancoSelectOption[]>(
    () => (bancosCatalogo ?? []).map((b: IBancoOption) => ({ value: b.slug, label: b.nome })),
    [bancosCatalogo],
  );
  const bancoValue = useMemo(
    () => bancoOptions.find(o => o.value === novoBanco) ?? null,
    [bancoOptions, novoBanco],
  );

  const corrigirBanco = async () => {
    if (!novoBanco || conta.id == null) return;
    try {
      const res = await reidentificar.mutateAsync({ id: conta.id, banco_nome: novoBanco });
      const partes: string[] = [];
      if (res.conciliados) partes.push(`${res.conciliados} conciliados`);
      toast({
        title: `Banco corrigido${partes.length ? ` · ${partes.join(' · ')}` : ''}`,
        status: 'success',
      });
      setTrocandoBanco(false);
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, 'Falha ao corrigir o banco.'), status: 'error' });
    }
  };

  const pocValue = useMemo(() => pocOptions.find(o => o.value === poc) ?? null, [pocOptions, poc]);
  const dirty = poc !== (conta.poc_codigo ?? null);
  const salvando = criar.isPending || editar.isPending;
  const semPoc = !conta.poc_codigo;

  const salvar = async () => {
    try {
      const res =
        conta.id == null
          ? await criar.mutateAsync({
              empresa_id: empresaId,
              banco_nome: conta.banco_nome,
              banco_id_febraban: conta.banco_id_febraban,
              conta: conta.conta,
              tipo: conta.tipo,
              poc_codigo: poc,
            })
          : await editar.mutateAsync({ id: conta.id, poc_codigo: poc });
      const aguardando = res.aguardando_contrapartida ?? 0;
      const partes: string[] = [];
      if (res.conciliados) partes.push(`${res.conciliados} conciliados`);
      if (aguardando) partes.push(`${aguardando} aguardando contrapartida`);
      toast({
        title: `Conta salva${partes.length ? ` · ${partes.join(' · ')}` : ''}`,
        description: aguardando
          ? 'Os “aguardando” já têm a perna do banco — falta classificar a contrapartida (não precisa reprocessar o extrato).'
          : undefined,
        status: 'success',
        duration: aguardando ? 7000 : undefined,
      });
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, 'Falha ao salvar.'), status: 'error' });
    }
  };

  return (
    <Tr _hover={{ bg: 'rgba(139, 92, 246, 0.04)' }}>
      <Td>
        <Text fontSize="sm" fontWeight="600" color="#0F172A">
          {conta.banco_nome}
        </Text>
        <Text fontSize="11px" color="#64748B">
          conta {conta.conta}
          {conta.agencia ? ` · ag ${conta.agencia}` : ''} ·{' '}
          {TIPO_CONTA_LABEL[conta.tipo] ?? conta.tipo}
        </Text>
        {conta.id != null && !podeCorrigirBanco && !trocandoBanco && (
          <Button
            size="xs"
            variant="link"
            colorScheme="purple"
            fontWeight="600"
            mt={1}
            onClick={() => setTrocandoBanco(true)}
          >
            Trocar banco
          </Button>
        )}
        {mostrarCorrecaoBanco && (
          <Flex gap={1.5} align="center" mt={1.5} maxW="320px">
            <Box flex="1" minW="180px">
              <RSelect<BancoSelectOption>
                size="sm"
                placeholder={
                  ehDuplicata && !naoIdentificado ? 'Mesclar no banco...' : 'Corrigir banco...'
                }
                options={bancoOptions}
                value={bancoValue}
                onChange={opt => setNovoBanco(opt ? opt.value : null)}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                {...propsMenuSelect}
                styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                chakraStyles={{ control: provided => ({ ...provided, borderRadius: 'lg' }) }}
              />
            </Box>
            <Button
              size="sm"
              borderRadius="full"
              variant="outline"
              colorScheme="purple"
              fontWeight="600"
              isDisabled={!novoBanco}
              isLoading={reidentificar.isPending}
              onClick={corrigirBanco}
            >
              Corrigir
            </Button>
          </Flex>
        )}
      </Td>
      <Td>
        <Flex gap={1.5} align="center" wrap="wrap">
          {conta.registrada ? (
            <Badge colorScheme="purple" borderRadius="full">
              cadastrada
            </Badge>
          ) : (
            <Badge colorScheme="orange" borderRadius="full">
              detectada
            </Badge>
          )}
          {semPoc && (
            <Badge
              colorScheme="yellow"
              borderRadius="full"
              title="Conta sem POC — não fecha sozinha"
            >
              sem POC
            </Badge>
          )}
        </Flex>
      </Td>
      <Td isNumeric fontSize="xs" color="#0F172A">
        {conta.qtd_lancamentos}
        {conta.pendentes > 0 && (
          <Text as="span" color="#B45309" fontWeight="600">
            {' '}
            ({conta.pendentes} pend.)
          </Text>
        )}
      </Td>
      <Td minW="280px">
        <RSelect<PocSelectOption>
          size="sm"
          placeholder="POC da conta do banco..."
          options={pocOptions}
          value={pocValue}
          onChange={opt => setPoc(opt ? opt.value : null)}
          isClearable
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          {...propsMenuSelect}
          styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
          chakraStyles={{ control: provided => ({ ...provided, borderRadius: 'lg' }) }}
        />
      </Td>
      <Td>
        <Button
          size="sm"
          borderRadius="full"
          bg="#8B5CF6"
          color="white"
          fontWeight="600"
          _hover={{ bg: '#7C3AED' }}
          isDisabled={!dirty && conta.id != null}
          isLoading={salvando}
          onClick={salvar}
        >
          {conta.id == null ? 'Cadastrar' : 'Salvar'}
        </Button>
      </Td>
    </Tr>
  );
}

/**
 * Tabela banco↔POC de uma empresa (lado banco da partida dobrada). Componente
 * compartilhado entre a tela /bancos e a aba "Contas" do hub Empresas — a
 * empresa vem por prop, sem AsyncSelect próprio.
 */
export function ContasTable({ empresaId }: { empresaId: number }) {
  const { data: contas = [], isFetching } = useContaCilContas(empresaId);
  const { data: pocs = [] } = useContaCilPocs(empresaId);
  const pocOptions = useMemo<PocSelectOption[]>(
    () => pocs.map((p: IPocOption) => ({ value: p.codigo, label: `${p.codigo} · ${p.nome}` })),
    [pocs],
  );
  // Chave da conta física (ag + conta normalizada) — mesma chave que o backend
  // usa pra achar duplicatas (mesma conta com rótulos de banco diferentes).
  const chaveConta = (c: IContaBancaria) =>
    `${(c.agencia ?? '').trim()}|${(c.conta ?? '').replace(/^0+/, '').replace(/[^0-9.-]/g, '')}`;
  const contasDuplicadas = useMemo<Set<string>>(() => {
    const cont = new Map<string, number>();
    contas.forEach(c => cont.set(chaveConta(c), (cont.get(chaveConta(c)) ?? 0) + 1));
    return new Set(
      Array.from(cont.entries())
        .filter(([, n]) => n > 1)
        .map(([k]) => k),
    );
  }, [contas]);

  return (
    <GlassCard intensity="medium" p={0} overflow="hidden">
      <Flex
        align="center"
        gap={2}
        px={4}
        py={3}
        borderBottomWidth="1px"
        borderColor="rgba(15,23,42,0.06)"
      >
        <Text fontSize="sm" fontWeight="600" color="#0F172A">
          {contas.length} conta(s)
        </Text>
        {isFetching && <Spinner size="xs" color="#8B5CF6" thickness="2px" />}
      </Flex>
      {contas.length === 0 ? (
        <Box p={10}>
          <Text fontSize="sm" color="#64748B" textAlign="center">
            Nenhuma conta cadastrada ou detectada. Importe extratos para detectar as contas.
          </Text>
        </Box>
      ) : (
        <TableContainer>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Banco / Conta</Th>
                <Th>Status</Th>
                <Th isNumeric>Lançamentos</Th>
                <Th>POC da conta (lado banco)</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {contas.map(c => (
                <ContaRow
                  key={`${c.banco_nome}-${c.conta}-${c.id ?? 'novo'}`}
                  conta={c}
                  empresaId={empresaId}
                  pocOptions={pocOptions}
                  ehDuplicata={contasDuplicadas.has(chaveConta(c))}
                />
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </GlassCard>
  );
}
